import { useState, useCallback, useRef } from 'react';

type UseInfiniteScrollOptions = {
    hasMore?: boolean;
    loadMore?: () => Promise<unknown>;
    threshold?: number;
};

export function useInfiniteScroll({ hasMore, loadMore, threshold = 0.8 }: UseInfiniteScrollOptions) {
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const isFetchingRef = useRef(false);

    const onScroll = useCallback(
        async (e: React.UIEvent<HTMLElement>) => {
            if (isFetchingRef.current || !hasMore || !loadMore) return;

            const el = e.currentTarget;
            const maxScroll = el.scrollHeight - el.clientHeight;
            if (maxScroll > 0 && el.scrollTop > threshold * maxScroll) {
                isFetchingRef.current = true;
                setIsFetchingMore(true);
                try {
                    await loadMore();
                } finally {
                    // Let the DOM settle (new rows render, Loader removed) before re-enabling.
                    // Without this, the scroll event from the DOM mutation re-triggers loadMore.
                    setTimeout(() => {
                        isFetchingRef.current = false;
                        setIsFetchingMore(false);
                    }, 200);
                }
            }
        },
        [hasMore, loadMore, threshold],
    );

    return { onScroll, isFetchingMore };
}
